// frontend/src/components/detail/JobSummaries.jsx
import React from 'react';
import { Box, Grid } from '@mui/material';

// ⬅️ These are the same components used by the dashboard
import SectionHeader from '../SectionHeader';
import JobMapBoard from '../tiles/JobMapBoard';
import NextJobTile from '../tiles/NextJobTile';

// Small helper to locate a section by its title
const findByTitle = (sections, title) =>
  (sections || []).find((s) => String(s.title || '').trim().toLowerCase() === title.toLowerCase()) || null;

/**
 * EXACTLY match dashboard rendering:
 * - "The Job Map" uses <JobMapBoard columns={section.columns} />
 * - "Next Jobs" uses the same grid → each tile has type "nextJob" and is rendered by <NextJobTile {...t} />
 * - Pass section.section_total_interviews (or sectionTotal) into tiles as sectionTotal (same as TilesShowcase)
 */
export default function JobSummaries({ sections }) {
  if (!Array.isArray(sections) || !sections.length) return null;

  const jobMap = findByTitle(sections, 'The Job Map');
  const nextJobs = findByTitle(sections, 'Next Jobs');

  const renderTilesGrid = (tiles, sectionTotal) => (
    <Grid container spacing={2}>
      {(tiles || []).map((t, i) => {
        // Dashboard uses a registry; for Next Jobs the type is "nextJob"
        // Render with the same card component and same responsive grid sizing
        const gridProps = t.type === 'jobSatisfactionSummary' ? { xs: 12 } : { xs: 12, sm: 6, md: 4, lg: 3 };
        return (
          <Grid item key={i} {...gridProps}>
            <NextJobTile {...t} sectionTotal={sectionTotal} />
          </Grid>
        );
      })}
    </Grid>
  );

  const sectionTotalFrom = (sec) =>
    sec?.section_total_interviews ?? sec?.sectionTotal ?? 0;

  const hasAnything = !!jobMap || !!nextJobs;
  if (!hasAnything) return null;

  return (
    <Box sx={{ maxWidth: 1200 }}>
      {/* The Job Map — same wrapper + same board component */}
      {jobMap && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            metaTitle={jobMap.metaTitle ?? null}
            title={jobMap.title}
            intro={null}
            updatedAt={jobMap.updatedAt}
          />
          <JobMapBoard columns={jobMap.columns || []} />
        </Box>
      )}

      {/* Next Jobs — same wrapper + same tile grid behavior */}
      {nextJobs && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            metaTitle={nextJobs.metaTitle ?? null}
            title={nextJobs.title}
            intro={null}
            updatedAt={nextJobs.updatedAt}
          />
          {renderTilesGrid(nextJobs.tiles || [], sectionTotalFrom(nextJobs))}
        </Box>
      )}
    </Box>
  );
}
